import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Eye, Sparkles, Rocket, BarChart3, Loader2, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { makeLayer } from "@/components/layers";
import { EditorCanvas } from "@/components/editor/EditorCanvas";
import { PageThumbs } from "@/components/editor/PageThumbs";
import { ToolBar } from "@/components/editor/ToolBar";
import { PropertiesPanel } from "@/components/editor/PropertiesPanel";
import { LayersPanel } from "@/components/editor/LayersPanel";
import { AiPanel } from "@/components/editor/AiPanel";
import { PublishDialog } from "@/components/editor/PublishDialog";
import { QuizResultsDialog } from "@/components/editor/QuizResultsDialog";
import { PagesDialog } from "@/components/editor/PagesDialog";
import { PageTextPanel } from "@/components/editor/PageTextPanel";
import { Progress } from "@/components/ui/progress";
import { LogoIcon, DevCredit } from "@/components/landing/Logo";
import { LoaderScreen, BookLoader } from "@/components/BrandLoader";

const SUGGESTION_TYPE = { audio: "audio", video: "video", hotspot: "hotspot", image: "hotspot", popup: "popup", quiz: "quiz", link: "link", animation: "text" };

const PanelTab = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`border-b-2 pb-2 text-xs transition-colors ${active ? "border-[#3D3DF0] text-[#0F172A]" : "border-transparent text-[#64748B] hover:text-[#0F172A]"}`}>{children}</button>
);

const TopBtn = ({ onClick, icon: Icon, children, testid, primary, disabled }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid} className={`flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-medium disabled:opacity-60 ${primary ? "neu-btn-primary" : "neu-btn"}`}>
    <Icon className="h-3.5 w-3.5" /><span className="hidden lg:inline">{children}</span>
  </button>
);

export default function Editor() {
  const { bookId } = useParams();
  const [book, setBook] = useState(null);
  const [layers, setLayers] = useState({});
  const [current, setCurrent] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState("properties");
  const [saveState, setSaveState] = useState("saved");
  const [publishOpen, setPublishOpen] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [pagesOpen, setPagesOpen] = useState(false);
  const dirty = useRef(false);

  const load = useCallback(() => api.get(`/books/${bookId}`).then(({ data }) => {
    setBook(data);
    setLayers((l) => (dirty.current ? l : data.layers || {}));
    setCurrent((c) => Math.min(c, Math.max(1, data.pages.length)));
    if (data.status === "ready" && data.error) toast.error(data.error);
  }).catch((e) => toast.error(errMsg(e))), [bookId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (book?.status !== "processing") return;
    const t = setTimeout(load, 1500);
    return () => clearTimeout(t);
  }, [book, load]);

  const save = useCallback(async () => {
    setSaveState("saving");
    try {
      await api.put(`/books/${bookId}/layers`, { layers });
      dirty.current = false;
      setSaveState("saved");
    } catch (e) {
      setSaveState("error");
      toast.error(errMsg(e));
    }
  }, [bookId, layers]);

  useEffect(() => {
    if (!dirty.current) return;
    const t = setTimeout(save, 1200);
    return () => clearTimeout(t);
  }, [layers, save]);

  const pageLayers = layers[current] || [];
  const selected = pageLayers.find((l) => l.id === selectedId);

  const mutate = useCallback((page, fn) => {
    dirty.current = true;
    setSaveState("unsaved");
    setLayers((all) => ({ ...all, [page]: fn(all[page] || []) }));
  }, []);

  const add = (type, extra, page = current) => {
    const l = makeLayer(type, extra);
    mutate(page, (ls) => [...ls, l]);
    setSelectedId(l.id);
    setTab("properties");
  };
  const change = useCallback((id, patch) => mutate(current, (ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l))), [current, mutate]);
  const remove = (id) => { mutate(current, (ls) => ls.filter((l) => l.id !== id)); setSelectedId(null); };
  const duplicate = (id) => {
    const src = pageLayers.find((l) => l.id === id);
    add(src.type, { ...src, id: undefined, name: `${src.name} copy`, x: Math.min(src.x + 3, 100 - src.w), y: Math.min(src.y + 3, 100 - src.h), locked: false });
  };
  const moveLayer = (id, dir) => mutate(current, (ls) => {
    const i = ls.findIndex((l) => l.id === id), j = i + dir;
    if (j < 0 || j >= ls.length) return ls;
    const c = [...ls];
    [c[i], c[j]] = [c[j], c[i]];
    return c;
  });

  const acceptSuggestion = (s) => {
    const type = SUGGESTION_TYPE[s.type] || "hotspot";
    const extra = { name: s.title, x: s.x, y: s.y, w: Math.max(s.w, 5), h: Math.max(s.h, 4) };
    if (type === "hotspot") Object.assign(extra, { action: "popup", title: s.title, content: s.content || s.reason, w: 7, h: 5 });
    if (type === "popup") Object.assign(extra, { title: s.title, content: s.content || s.reason, w: 7, h: 5 });
    if (type === "quiz") {
      Object.assign(extra, { title: s.title, w: 22, h: 5 });
      if (s.quiz?.questions?.length) extra.questions = s.quiz.questions;
    }
    if (type === "audio") Object.assign(extra, { content: s.content, alt: `Listen: ${s.title}`, w: 7, h: 5 });
    if (type === "text") Object.assign(extra, { content: s.title });
    if (type === "video") Object.assign(extra, { mode: "popup" });
    setCurrent(s.page);
    const l = makeLayer(type, extra);
    mutate(s.page, (ls) => [...ls, l]);
    setSelectedId(l.id);
    toast.success(`Added ${s.type} to page ${s.page}`);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName) || e.target.isContentEditable) return;
      if ((e.key === "Delete" || e.key === "Backspace") && selectedId) remove(selectedId);
      if ((e.metaKey || e.ctrlKey) && e.key === "s") { e.preventDefault(); save(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const rename = async (title) => {
    if (!title || title === book.title) return;
    const { data } = await api.patch(`/books/${bookId}`, { title });
    setBook((b) => ({ ...b, title: data.title }));
  };

  const preview = async () => {
    const w = window.open("", "_blank");
    await save();
    w.location.href = `/preview/${bookId}`;
  };

  if (!book) return <LoaderScreen label="Opening the editor" testid="editor-loading" />;

  if (book.status !== "ready") {
    const pct = book.progress?.total ? (book.progress.done / book.progress.total) * 100 : 3;
    return (
      <div className="grid h-screen place-items-center bg-[#EEF2F8]" data-testid="editor-processing">
        <div className="w-80 text-center">
          {book.status !== "failed" && <div className="bl-float mx-auto mb-6 grid h-24 w-24 place-items-center rounded-[1.75rem] neu-raised"><BookLoader size={42} /></div>}
          <p className="font-mono text-xs uppercase tracking-widest text-[#3D3DF0]">{book.status === "failed" ? "Processing failed" : "Ingestion engine"}</p>
          <h2 className="mt-3 font-display text-3xl font-extrabold">{book.title}</h2>
          {book.status === "failed" ? <p className="mt-4 text-sm text-[#A62B8A]">{book.error || "Could not process this PDF."}</p> : (
            <>
              <Progress value={pct} className="mt-6 h-1" />
              <p className="mt-3 font-mono text-xs text-[#64748B]">Rendering WebP pages {book.progress?.done || 0}/{book.progress?.total || "…"}</p>
            </>
          )}
          <Link to="/dashboard" className="mt-8 inline-block text-xs text-[#64748B] hover:text-[#0F172A]">← Back to library</Link>
        </div>
      </div>
    );
  }

  const page = book.pages.find((p) => p.index === current);

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-[#EEF2F8]" data-testid="editor-page">
      <header className="z-20 flex h-16 shrink-0 items-center justify-between gap-3 bg-[#EEF2F8]/80 shadow-[0_6px_16px_-10px_rgba(166,180,208,.9)] px-4 backdrop-blur-xl">
        <div className="flex min-w-0 items-center gap-3">
          <Link to="/dashboard" className="rounded p-2 text-[#64748B] hover:bg-[#E2E8F3] hover:text-[#0F172A]" aria-label="Back to library" data-testid="editor-back-btn"><ArrowLeft className="h-4 w-4" /></Link>
          <Link to="/dashboard" className="hidden shrink-0 sm:block" aria-label="iBookStudio home" data-testid="editor-brand-link"><LogoIcon className="h-8" /></Link>
          <input defaultValue={book.title} onBlur={(e) => rename(e.target.value.trim())} className="min-w-0 max-w-[260px] rounded bg-transparent px-2 py-1 text-sm hover:bg-[#E2E8F3] focus:bg-[#E2E8F3] focus:outline-none" data-testid="editor-title-input" />
          <span className="hidden items-center gap-1 font-mono text-[11px] text-[#64748B] md:flex" data-testid="save-status">
            {saveState === "saving" ? <Loader2 className="h-3 w-3 animate-spin" /> : saveState === "saved" ? <Check className="h-3 w-3 text-[#5FA82E]" /> : null}
            {saveState === "saved" ? "Saved" : saveState === "saving" ? "Saving" : saveState === "error" ? "Save failed" : "Unsaved"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <TopBtn onClick={save} icon={Save} testid="editor-save-btn">Save</TopBtn>
          <TopBtn onClick={preview} icon={Eye} testid="editor-preview-btn">Preview</TopBtn>
          <TopBtn onClick={() => setTab("ai")} icon={Sparkles} testid="editor-ai-btn">AI Suggestions</TopBtn>
          <TopBtn onClick={() => setResultsOpen(true)} icon={BarChart3} testid="editor-quiz-results-btn">Analytics</TopBtn>
          <TopBtn onClick={() => setPublishOpen(true)} icon={Rocket} primary testid="editor-publish-btn">Publish</TopBtn>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        <PageThumbs pages={book.pages} current={current} layers={layers} onPick={(i) => { setCurrent(i); setSelectedId(null); }} onOrganize={() => setPagesOpen(true)} />
        <main className="relative flex min-w-0 flex-1 flex-col">
          <EditorCanvas page={page} layers={pageLayers} selectedId={selectedId} onSelect={setSelectedId} onChange={change} />
          <div className="pointer-events-none absolute inset-x-0 bottom-5 flex flex-col items-center gap-3">
            <div className="pointer-events-auto flex items-center gap-2 rounded-full neu-flat px-2 py-1 font-mono text-xs">
              <button onClick={() => current > 1 && setCurrent(current - 1)} aria-label="Previous page" data-testid="editor-prev-page" className="rounded-full p-1 hover:bg-[#E2E8F3]"><ChevronLeft className="h-4 w-4" /></button>
              <span data-testid="editor-page-indicator">{current} / {book.pages.length}</span>
              <button onClick={() => current < book.pages.length && setCurrent(current + 1)} aria-label="Next page" data-testid="editor-next-page" className="rounded-full p-1 hover:bg-[#E2E8F3]"><ChevronRight className="h-4 w-4" /></button>
            </div>
            <ToolBar onAdd={(t) => add(t)} />
          </div>
        </main>
        <aside className="flex w-[300px] shrink-0 flex-col m-3 ml-0 rounded-3xl neu-raised overflow-hidden" data-testid="right-panel">
          <div className="flex gap-5 border-b border-[#DCE3EE] px-4 pt-4">
            <PanelTab active={tab === "properties"} onClick={() => setTab("properties")} testid="tab-properties">Properties</PanelTab>
            <PanelTab active={tab === "layers"} onClick={() => setTab("layers")} testid="tab-layers">Layers · {pageLayers.length}</PanelTab>
            <PanelTab active={tab === "text"} onClick={() => setTab("text")} testid="tab-text">Text</PanelTab>
            <PanelTab active={tab === "ai"} onClick={() => setTab("ai")} testid="tab-ai">AI</PanelTab>
          </div>
          <div className="flex-1 overflow-y-auto p-4 thin-scroll">
            {tab === "properties" && <PropertiesPanel layer={selected} bookId={bookId} onChange={change} />}
            {tab === "layers" && <LayersPanel layers={pageLayers} selectedId={selectedId} onSelect={(id) => { setSelectedId(id); }} onChange={change} onDuplicate={duplicate} onDelete={remove} onMove={moveLayer} />}
            {tab === "text" && <PageTextPanel bookId={bookId} page={current} />}
            {tab === "ai" && <AiPanel bookId={bookId} onAccept={acceptSuggestion} />}
          </div>
        </aside>
      </div>
      <DevCredit className="shrink-0 border-t border-[#DCE3EE] py-1" />
      <PagesDialog open={pagesOpen} onOpenChange={setPagesOpen} book={book} beforeChange={async () => { if (dirty.current) await save(); }} onDone={() => { setSelectedId(null); load(); }} />
      <QuizResultsDialog open={resultsOpen} onOpenChange={setResultsOpen} bookId={bookId} />
      <PublishDialog open={publishOpen} onOpenChange={setPublishOpen} book={book} onBookChange={(p) => setBook((b) => ({ ...b, ...p }))} beforePublish={save} />
    </div>
  );
}
